import { Field, useFormikContext } from 'formik'
import { get } from 'lodash'
import React from 'react'
import { AddressSearch } from 'components/AddressSearch'
import { CountrySearch } from 'components/CountrySearch'
import { AnimatedInputWrapper, Input } from 'components/Input'
import { PhoneInput } from 'components/PhoneInput'
import { StateSearch } from 'components/StateSearch'
import { EMPTY_STATE_OPTION } from 'components/StateSearch/constants'
import { type Country } from 'types/graphql-generated'
import { ADDRESS_FIELD_NAMES } from './AddressFieldsValidationSchema'

interface AdrressFieldsProps {
  prefix: string
  isDisabled?: boolean
  isLoading?: boolean
  isCountryInputDisabled?: boolean
  isTradeLicensePickerVisible?: boolean
}

export const AddressFields: React.FC<React.PWC<AdrressFieldsProps>> = ({
  prefix,
  isDisabled,
  isLoading,
  isCountryInputDisabled,
  isTradeLicensePickerVisible = false,
}) => {
  const { values: formValues } = useFormikContext()
  const isStateInputVisible =
    get(formValues, [
      prefix,
      ADDRESS_FIELD_NAMES.COUNTRY,
      'value',
      'isoCode',
    ]) === 'us'

  const isCompanyFieldVisible =
    get(formValues, [prefix, ADDRESS_FIELD_NAMES.COMPANY], null) !== null

  const isSecondAddressFieldVisible =
    get(formValues, [prefix, ADDRESS_FIELD_NAMES.ADDRESS_LINE2], null) !== null

  return (
    <>
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.FULL_NAME}`}>
        {({ field, meta }) => (
          <Input
            label="Full Name"
            isError={meta.touched && meta.error !== undefined}
            hint={meta.touched && meta.error ? meta.error : null}
            hasHint={isCompanyFieldVisible}
            isDisabled={isDisabled || isLoading}
            autoComplete="name"
            {...field}
          />
        )}
      </Field>
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.COMPANY}`}>
        {({ field, meta, form: { setFieldValue } }) => (
          <AnimatedInputWrapper
            label="Add company"
            isVisible={isCompanyFieldVisible}
            setIsVisible={() => {
              setFieldValue(`${prefix}.${ADDRESS_FIELD_NAMES.COMPANY}`, '')
            }}
          >
            <Input
              label="Company (optional)"
              isError={meta.touched && meta.error !== undefined}
              hint={meta.touched && meta.error ? meta.error : null}
              hasHint
              isDisabled={isDisabled || isLoading}
              autoComplete="organization"
              {...field}
            />
          </AnimatedInputWrapper>
        )}
      </Field>
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.ADDRESS_LINE1}`}>
        {({ field, meta, form: { setFieldValue, values } }) => (
          <AddressSearch
            label="Address Line 1"
            isError={meta.touched && meta.error !== undefined}
            hint={meta.touched && meta.error ? meta.error : null}
            hasHint={isSecondAddressFieldVisible}
            isDisabled={isDisabled || isLoading}
            autoComplete="address-line1"
            onSelectFromSuggestions={async ({
              value: { title, zipCode, city, state, country },
            }) => {
              if (zipCode) {
                setFieldValue(
                  `${prefix}.${ADDRESS_FIELD_NAMES.ZIP_CODE}`,
                  zipCode
                )
              }
              if (city) {
                await setFieldValue(
                  `${prefix}.${ADDRESS_FIELD_NAMES.CITY}`,
                  city.title
                )
              }
              if (state && country?.isoCode === 'us') {
                setFieldValue(`${prefix}.${ADDRESS_FIELD_NAMES.STATE}`, {
                  label: state.title,
                  value: state,
                })
                if (isTradeLicensePickerVisible) {
                  setFieldValue('tradeLicense', {})
                }
              }
              if (country && !isCountryInputDisabled) {
                setFieldValue(`${prefix}.${ADDRESS_FIELD_NAMES.COUNTRY}`, {
                  label: country.title,
                  value: country,
                })
              }
              // We need to set the address line 1 last and trigger validation with new values
              await setFieldValue(field.name, title, true)
            }}
            countryIsoCode={get(values, [
              prefix,
              ADDRESS_FIELD_NAMES.COUNTRY,
              'value',
              'isoCode',
            ])}
            {...field}
          />
        )}
      </Field>
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.ADDRESS_LINE2}`}>
        {({ field, meta, form: { setFieldValue } }) => (
          <AnimatedInputWrapper
            label="Add address line 2"
            isVisible={isSecondAddressFieldVisible}
            setIsVisible={() => {
              setFieldValue(
                `${prefix}.${ADDRESS_FIELD_NAMES.ADDRESS_LINE2}`,
                ''
              )
            }}
          >
            <Input
              label="Address Line 2 (optional)"
              isError={meta.touched && meta.error !== undefined}
              hint={meta.touched && meta.error ? meta.error : null}
              hasHint
              isDisabled={isDisabled || isLoading}
              autoComplete="address-line2"
              {...field}
            />
          </AnimatedInputWrapper>
        )}
      </Field>
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.ZIP_CODE}`}>
        {({ field, meta }) => (
          <Input
            label="ZIP Code"
            isError={meta.touched && meta.error !== undefined}
            hint={meta.touched && meta.error ? meta.error : null}
            hasHint
            isDisabled={isDisabled || isLoading}
            autoComplete="postal-code"
            {...field}
          />
        )}
      </Field>
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.CITY}`}>
        {({ field, meta }) => (
          <Input
            label="City"
            isError={meta.touched && meta.error !== undefined}
            hint={meta.touched && meta.error ? meta.error : null}
            hasHint
            isDisabled={isDisabled || isLoading}
            autoComplete="address-level2"
            {...field}
          />
        )}
      </Field>
      {isStateInputVisible && (
        <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.STATE}`}>
          {({ field, meta, form: { setFieldValue } }) => (
            <StateSearch
              label="State"
              isError={meta.touched && meta.error !== undefined}
              hint={
                meta.touched && meta.error?.value?.title
                  ? meta.error?.value?.title
                  : null
              }
              hasHint
              isDisabled={isDisabled || isLoading}
              autoComplete="address-level1"
              {...field}
              onChange={(state) => {
                setFieldValue(field.name, state)
                if (isTradeLicensePickerVisible) {
                  setFieldValue('tradeLicense', {})
                }
              }}
              name={`${field.name}.value`}
            />
          )}
        </Field>
      )}
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.COUNTRY}`}>
        {({ field, meta, form: { setFieldValue } }) => (
          <CountrySearch
            label="Country"
            isError={meta.touched && meta.error !== undefined}
            hint={
              meta.touched && meta.error?.value?.isoCode
                ? meta.error?.value?.isoCode
                : null
            }
            hasHint
            isDisabled={isCountryInputDisabled || isDisabled || isLoading}
            autoComplete="country"
            {...field}
            onChange={(country: Country) => {
              setFieldValue(field.name, country)
              if (country.isoCode !== 'us') {
                setFieldValue(
                  `${prefix}.${ADDRESS_FIELD_NAMES.STATE}`,
                  EMPTY_STATE_OPTION
                )
              }
            }}
            name={`${field.name}.value`}
          />
        )}
      </Field>
      <Field name={`${prefix}.${ADDRESS_FIELD_NAMES.PHONE}`}>
        {({ field, meta, form: { setFieldValue } }) => (
          <PhoneInput
            label="Phone"
            name={field.name}
            value={field.value}
            onChange={(value) => {
              setFieldValue(field.name, value)
            }}
            hasHint
            hint={meta.touched && meta.error ? meta.error : null}
            isError={meta.touched && meta.error !== undefined}
            onBlur={field.onBlur}
            isDisabled={isDisabled || isLoading}
            tooltip="We’ll contact this number in case of any issues with this order"
          />
        )}
      </Field>
    </>
  )
}
